import { Component, OnInit, OnDestroy } from '@angular/core';
import { PublisherService } from '../../../core/services/publisher.service';
import { OktaService } from '../okta.service';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { OktaOperation } from '../okta-operation';
import {
  CacheConstant,
  OktaOperationType,
  OktaOperationSource,
  Regex,
  CacheDuration
} from '../../../core/constants/app.constant';
import { NgForm } from '@angular/forms';
import { AppUtilService } from '../../../core/utils/app-util.service';
import { HttpError } from '../../../shared/models/http-error';
import { AuthService } from '../../../core/services/auth.service';
import { OktaRoutes, AEMRoutes } from '../../../core/constants/routes.constant';
import { OktaConstant } from '../okta.constant';
import { OktaAuthService } from '../../../core/services/okta-auth.service';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { AppCacheService } from '../../../core/services/cache/app-cache.service';
import { Router } from '@angular/router';

@Component({
  selector: 'par-activate-user',
  templateUrl: './activate-user.component.html',
  styleUrls: ['./activate-user.component.scss']
})
export class ActivateUserComponent implements OnInit, OnDestroy {
  /***************Properties***************/
  envProperties =  this.cache.get(CacheConstant.Env_Properties);
  private activateSubscription: Subscription = null;
  private operation: OktaOperation = null;
  websiteName: string = this.envProperties.theme;
  messages: any = {
    passwordHint: OktaConstant.Messages.ActivateUserPasswordHintMessage
  };
  validation: any = {
    passwordRegex: Regex.Password,
    answerRegex: OktaConstant.SecurityAnswer.LengthRegex,
    ansMinLength: OktaConstant.SecurityAnswer.MinLength,
    ansInfoMessage: OktaConstant.SecurityAnswer.ValidationMessage
  };
  securityQuestions: Array<any> = new Array<any>();
  activate = {
    firstname: '',
    newPassword: '',
    confirmPassword: '',
    question: '',
    answer: '',
    confirmAnswer: '',
    errorMessage: '',
    token: {
      expired: false,
      message: ''
    }
  };
  resetPasswordUrl: string = '';
  /***************Properties***************/

  /***************Constructor***************/
  constructor(
    private cache: AppCacheService,
    private oktaService: OktaService,
    private route: ActivatedRoute,
    private oktaAuthService: OktaAuthService,
    private loader: LoadingBarService,
    private router: Router) {}

  ngOnInit() {

    if (this.router.url.includes('reset_password')) {
        this.resetPasswordUrl = 'resetpassword'; 
    }
    
    this.activateSubscription = this.route.params.subscribe(params => {
      if (params.token) {
        this.setOktaCacheOperation({ token: params.token });
        AppUtilService.router.navigate([OktaRoutes.VerifyToken]);
      } else {
        this.operation = AppUtilService.cache.get(
          CacheConstant.Okta_Operation
        ) as OktaOperation;

        if (
          !this.operation ||
          !this.operation.data ||
          !this.operation.data.verified
        ) {
          AppUtilService.redirectToHome();
        } else {
          let bannerTitle = this.isActivateUser() ? 'Activate User' : '';
          if(this.resetPasswordUrl === 'resetpassword') {
            bannerTitle = 'Reset Password';
          }
          const title = bannerTitle;
          PublisherService.changeBannerHeader(title);
          AppUtilService.cache.remove(CacheConstant.Okta_Operation);
          this.activate.firstname = this.operation.data.firstName;
          this.getSecurityQuestions();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.activateSubscription.unsubscribe();
  }
  /***************Constructor***************/

  /***************Component Methods***************/

  isActivateUser() {
    return (
      !this.operation || this.operation.type === OktaOperationType.ActivateUser || this.operation.type === OktaOperationType.ResetPwd
    );
  }

  onActivateUserSubmit(activateForm: NgForm) {
    if (activateForm.invalid) {
      return;
    }

    this.activate.errorMessage = '';
    const activateObj = {
      oktaId: this.operation.data.oktaId,
      password: this.activate.newPassword,
      recoveryAnswer: this.activate.answer,
      recoveryQuestion: this.activate.question,
      stateToken: this.operation.data.stateToken,
      buId: this.envProperties.buId,
      action: this.resetPasswordUrl
    };

    const activateApi = this.isActivateUser()
      ? this.oktaService.activate(activateObj)
      : this.oktaService.activateNonMigrate(activateObj);

    activateApi.subscribe((response: any) => {
      if (response && response.hasError) {
        this.regenerateStateToken();
        this.activate.errorMessage = AppUtilService.resolveErrorResponse(
          response as HttpError
        ).messageText;
      } else {
        if (this.resetPasswordUrl === 'resetpassword') {
          AppUtilService.message.success(
            OktaConstant.Messages.ResetPasswordSuccessMesssage
          );
          AppUtilService.redirectToHome();
          setTimeout(() => {
            PublisherService.notifySignInRequest(null);
          }, 20);
        } else {
          AppUtilService.message.success(
            OktaConstant.Messages.UserActivationSuccessMesssage
          );
          this.authenticateUser(response.userId);
        }
        
      }
    });
  }

  /***************Component Methods***************/

  /***************Private Methods***************/

  private setOktaCacheOperation(data: any) {
    if(this.resetPasswordUrl === 'resetpassword') {
      const activateOperation: OktaOperation = {
        type: OktaOperationType.ResetPwd,
        source: OktaOperationSource.ResetPwd,
        data: data
      };
      AppUtilService.cache.set(
        CacheConstant.Okta_Operation,
        activateOperation,
        CacheDuration.OneMinute
      );
    }
    else {
      const activateOperation: OktaOperation = {
        type: OktaOperationType.ActivateUser,
        source: OktaOperationSource.ActivateUser,
        data: data
      };
      AppUtilService.cache.set(
        CacheConstant.Okta_Operation,
        activateOperation,
        CacheDuration.OneMinute
      );
    }
    
  }

  private getSecurityQuestions() {
    this.oktaService
      .getSecurityQuestions(this.operation.data.oktaId)
      .subscribe((response: any) => {
        if (!response.hasError) {
          this.securityQuestions = response;
        }
      });
  }

  private authenticateUser(userId: string) {
    this.oktaAuthService.signIn(userId, this.activate.newPassword).then(res => {
      if (!res.errorCode) {
        this.oktaAuthService
          .authorize(res)
          .then(tokens => {
            this.loader.start();
            AppUtilService.cache.set(CacheConstant.User_Id, userId);
            PublisherService.notifyLoadUserInfo();
          })
          .catch(error => {
            AppUtilService.message.error(
              this.oktaAuthService.getErrorMessageByCode(error.errorCode) ||
                error.message
            );
          });
      } else {
        AppUtilService.message.error(
          this.oktaAuthService.getErrorMessageByCode(res.errorCode) ||
            res.message
        );
      }
    });
  }

  /*
  Added this method to regenerate state token based on recovery token.
  Scenario: In some case, if okta throws any exeption, state token gets expired
            and for subsequent requests, it will show an OKTA error though inputs are valid.
   */
  private regenerateStateToken() {
    const token = this.operation.data.token;
    this.oktaService
      .verifyToken(this.operation.data.token)
      .subscribe((response: any) => {
        if (!response.hasError) {
          this.operation.data.stateToken =
            response.stateToken || this.operation.data.stateToken;
        } else {
          this.activate.token.expired = true;

          if(this.websiteName === 'Parmed') {
            this.activate.token.message =
            this.operation.type === OktaOperationType.ActivateUser
              ? OktaConstant.Messages.ActivationTokenExpiredMessage
              : OktaConstant.Messages.NonMigrateTokenExpiredMessage;
          }
          else if(this.websiteName === 'Major'){
            this.activate.token.message =
            this.operation.type === OktaOperationType.ActivateUser
              ? OktaConstant.Messages.MajorActivationTokenExpiredMessage
              : OktaConstant.Messages.NonMigrateTokenExpiredMessage;
          }
         
        }
      });
  }

  /***************Private Methods***************/
}
